"use client";

import { useEffect, useMemo, useState } from "react";

type Source = "x" | "youtube" | "ecommerce";

export type KeywordProfile = {
  id: string;
  source: Source;
  name: string;
  keywords: string[];
  settings: Record<string, any>;
};

const STORAGE_KEY = "osint_keyword_profiles";

export function useKeywordProfiles() {
  const [profiles, setProfiles] = useState<KeywordProfile[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as KeywordProfile[];
        setProfiles(parsed);
        setActiveProfileId(parsed[0]?.id ?? null);
        return;
      }
    } catch (e) {
      console.error("Failed to load profiles", e);
    }
  }, []);

  useEffect(() => {
    if (profiles.length === 0) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
  }, [profiles]);

  const activeProfile = useMemo(() => {
    return profiles.find((p) => p.id === activeProfileId) ?? null;
  }, [profiles, activeProfileId]);

  const createProfile = (source: Source) => {
    const id = crypto.randomUUID();
    const newProfile: KeywordProfile = {
      id,
      source,
      name: `New ${source.toUpperCase()} Profile`,
      keywords: [],
      settings: {},
    };
    setProfiles((prev) => [newProfile, ...prev]);
    setActiveProfileId(id);

    return newProfile;
  };

  const updateProfile = (id: string, payload: Partial<KeywordProfile>) => {
    setProfiles((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              ...payload,
              keywords: payload.keywords ?? p.keywords,
              settings: payload.settings ?? p.settings,
            }
          : p,
      ),
    );
  };

  const deleteProfile = (id: string) => {
    setProfiles((prev) => {
      const next = prev.filter((p) => p.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });

    setActiveProfileId((prevId) => {
      const nextId = prevId === id ? null : prevId;
      localStorage.setItem("osint_active_profile_id", nextId ?? "");
      return nextId;
    });
  };

  return {
    profiles,
    activeProfile,
    activeProfileId,
    setActiveProfileId,
    createProfile,
    updateProfile,
    deleteProfile,
  };
}
